import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { biApi } from '../api/bi'
import { colors, font, text, space, radius, sx } from '../design/tokens'
import { fmtAbrev, fmtNum } from '../utils/bi'
import { fmtFecha } from '../utils/fecha'
import Boton from '../components/Boton'

// ─── Tablero de Dirección (/bi) ──────────────────────────────────────
// Pulso del negocio (día + mes + proyección, por canal) + bandeja de
// hallazgos del motor BI (bi:detectar-hallazgos, corre cada noche).
// Diseño: docs/diseno-bi-potente.md — respuestas masticadas, no tablas en blanco.

const SEVERIDAD = {
  alta: { label: 'ALTA', color: colors.err, bg: colors.errBg },
  media: { label: 'MEDIA', color: colors.warn, bg: colors.warnBg },
  baja: { label: 'BAJA', color: colors.info, bg: colors.infoBg },
}

const pctTxt = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toLocaleString('es-AR', { maximumFractionDigits: 1 })}%`)

function VarReal({ v }) {
  if (v == null) return <span style={{ color: colors.textMeta }}>—</span>
  return <span style={{ ...sx.mono, fontWeight: 600, color: v >= 0 ? colors.ok : colors.err }}>{pctTxt(v)}</span>
}

function KpiCard({ label, valor, sub, hijo }) {
  return (
    <div style={{ flex: 1, minWidth: 200, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.lg}px ${space.xl}px` }}>
      <div style={{ ...sx.cardTitle }}>{label}</div>
      <div style={{ ...sx.mono, fontSize: 24, fontWeight: 700, color: colors.text, marginTop: space.xs, marginBottom: 2 }}>{valor}</div>
      {sub && <div style={{ fontSize: text.meta, color: colors.textMeta }}>{sub}</div>}
      {hijo}
    </div>
  )
}

// ─── Hallazgo (tarjeta masticada) ────────────────────────────────────
function HallazgoCard({ h, onVisto, onDescartar }) {
  const [abierto, setAbierto] = useState(false)
  const sev = SEVERIDAD[h.severidad] || SEVERIDAD.media
  const terminado = h.estado === 'resuelto' || h.estado === 'descartado'

  return (
    <div style={{
      background: colors.bg, border: `1px solid ${colors.border}`, borderLeft: `3px solid ${sev.color}`,
      borderRadius: radius.md, padding: `${space.lg}px ${space.xl}px`, opacity: terminado ? 0.65 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
        <span style={{ ...sx.badge, color: sev.color, background: sev.bg }}>{sev.label}</span>
        {h.estado === 'nuevo' && <span style={{ ...sx.badge, color: colors.link, background: colors.linkBg }}>NUEVO</span>}
        {h.estado === 'resuelto' && <span style={{ ...sx.badge, color: colors.ok, background: colors.okBg }}>SE RECUPERÓ</span>}
        <span style={{ flex: 1, fontSize: text.body, fontWeight: 600, color: colors.text }}>{h.titulo}</span>
        <span style={{ ...sx.mono, fontSize: text.meta, color: colors.textMeta }}>detectado {fmtFecha(h.ultima_deteccion_at)}</span>
      </div>

      <div style={{ marginTop: space.sm, fontSize: text.sec, color: colors.textSec, whiteSpace: 'pre-line' }}>
        {abierto ? h.detalle : h.detalle.split('\n')[0]}
      </div>

      <div style={{ display: 'flex', gap: space.sm, marginTop: space.md, alignItems: 'center' }}>
        {h.detalle.includes('\n') && (
          <Boton variante="ghost" sm onClick={() => setAbierto(!abierto)}>{abierto ? 'Menos' : 'Ver semanas'}</Boton>
        )}
        {!terminado && h.estado === 'nuevo' && <Boton variante="secondary" sm onClick={() => onVisto(h)}>✓ Visto</Boton>}
        {!terminado && <Boton variante="ghost" sm onClick={() => onDescartar(h)}>Descartar 30 días</Boton>}
      </div>
    </div>
  )
}

// ─── Canal (Retail / Mayorista) ──────────────────────────────────────
function CanalCard({ c }) {
  const inf = c.inflacion || {}
  const proy = c.proyeccion || {}
  const filas = [
    ['El día', fmtAbrev(c.dia?.facturacion ?? 0), null],
    ['Mes acumulado', fmtAbrev(c.mes?.facturacion ?? 0), null],
    [`Mes anterior · ${c.mes_anterior?.etiqueta ?? ''}`, c.mes_anterior ? fmtAbrev(c.mes_anterior.facturacion ?? 0) : '—', c.mes_anterior?.var ?? null],
    ['vs año pasado (IPC)', inf.piso_ipc != null ? `piso ${fmtAbrev(inf.piso_ipc)}` : '—', inf.var_ipc_pct],
    ['vs año pasado (mis costos)', inf.piso_compras != null ? `piso ${fmtAbrev(inf.piso_compras)}` : '—', inf.var_compras_pct],
    ['Proyección de cierre', proy.proyeccion != null ? fmtAbrev(proy.proyeccion) : '—', proy.vs_anio_pasado],
  ]
  return (
    <div style={{ flex: 1, minWidth: 300, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: space.xl }}>
      <h3 style={{ ...sx.cardTitle, marginBottom: space.lg }}>{c.label} <span style={{ color: colors.textMeta, fontWeight: 400 }}>({c.grupo === 'TODAS' ? 'emp_0' : 'emp_9'})</span></h3>
      {filas.map(([label, valor, varPct], i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '7px 0', borderBottom: i < filas.length - 1 ? `1px solid ${colors.borderSoft}` : 'none' }}>
          <span style={{ fontSize: text.sec, color: colors.textSec }}>{label}</span>
          <span style={{ display: 'flex', gap: space.md, alignItems: 'baseline' }}>
            <span style={{ ...sx.mono, fontSize: text.body, fontWeight: 600, color: colors.text }}>{valor}</span>
            {varPct !== null && <VarReal v={varPct} />}
          </span>
        </div>
      ))}
      {proy.pct > 0 && <div style={{ fontSize: text.meta, color: colors.textMeta, marginTop: space.sm }}>{fmtNum(proy.pct)}% del mes ya facturado (curva histórica)</div>}
    </div>
  )
}

export default function BiTableroPage() {
  const navigate = useNavigate()
  const [pulso, setPulso] = useState(null)
  const [hallazgos, setHallazgos] = useState([])
  const [counts, setCounts] = useState({})
  const [vista, setVista] = useState('activos')
  const [loading, setLoading] = useState(true)

  const cargarHallazgos = useCallback((v) => {
    biApi.hallazgos({ estado: v }).then(({ data }) => {
      setHallazgos(data.data)
      setCounts(data.counts || {})
    }).catch(() => {})
  }, [])

  useEffect(() => {
    Promise.allSettled([
      biApi.pulso().then(({ data }) => setPulso(data)),
      biApi.hallazgos({ estado: vista }).then(({ data }) => { setHallazgos(data.data); setCounts(data.counts || {}) }),
    ]).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cambiarVista = (v) => { setVista(v); cargarHallazgos(v) }
  const marcarVisto = (h) => biApi.hallazgoVisto(h.id).then(() => cargarHallazgos(vista))
  const descartar = (h) => biApi.hallazgoDescartar(h.id).then(() => cargarHallazgos(vista))

  const activos = (counts.nuevo || 0) + (counts.visto || 0)
  const vistas = [
    ['activos', `Activos${activos ? ` (${activos})` : ''}`],
    ['resuelto', `Resueltos${counts.resuelto ? ` (${counts.resuelto})` : ''}`],
    ['descartado', 'Descartados'],
  ]

  return (
    <div style={{ padding: space['2xl'], maxWidth: 1240, margin: '0 auto', fontFamily: font.sans }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: space.md, marginBottom: space.xl }}>
        <div>
          <div style={{ fontSize: text.meta, fontWeight: 600, color: colors.textMeta, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Inteligencia comercial</div>
          <h1 style={{ margin: '2px 0 0', fontSize: text.title + 6, color: colors.text }}>Tablero de dirección</h1>
        </div>
        <div style={{ display: 'flex', gap: space.sm, alignItems: 'center' }}>
          {pulso?.fecha && (
            <span style={{ fontSize: text.meta, color: colors.textMeta, ...sx.mono, marginRight: space.md }}>
              {pulso.es_hoy ? 'datos de hoy' : `último día con datos: ${fmtFecha(pulso.fecha)}`}
            </span>
          )}
          <Boton variante="secondary" sm onClick={() => navigate('/bi/ventas')}>Ventas en detalle</Boton>
          <Boton variante="secondary" sm onClick={() => navigate('/bi/gastos')}>Gastos en detalle</Boton>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: space['3xl'], textAlign: 'center', color: colors.textMeta }}>Cargando…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xl }}>

          {/* ── Hallazgos: lo que el sistema detectó solo ── */}
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: space.lg, marginBottom: space.md }}>
              <h2 style={{ margin: 0, fontSize: text.title, color: colors.text }}>Hallazgos</h2>
              <span style={{ fontSize: text.sec, color: colors.textMeta }}>desvíos detectados cada noche por el motor BI</span>
              <span style={{ flex: 1 }} />
              {vistas.map(([v, label]) => (
                <button key={v} onClick={() => cambiarVista(v)}
                  style={{ ...sx.pill, ...(vista === v ? sx.pillActive : {}) }}>{label}</button>
              ))}
            </div>
            {hallazgos.length === 0 ? (
              <div style={{ background: colors.bgSoft, border: `1px dashed ${colors.border}`, borderRadius: radius.md, padding: space['2xl'], textAlign: 'center', color: colors.textSec, fontSize: text.sec }}>
                {vista === 'activos' ? 'Sin desvíos activos — el negocio viene sin caídas sostenidas. El motor vuelve a mirar esta noche.' : 'Nada por acá.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
                {hallazgos.map((h) => <HallazgoCard key={h.id} h={h} onVisto={marcarVisto} onDescartar={descartar} />)}
              </div>
            )}
          </div>

          {/* ── Pulso: día + mes + proyección ── */}
          {pulso && !pulso.sin_datos && (
            <div>
              <h2 style={{ margin: `0 0 ${space.md}px`, fontSize: text.title, color: colors.text }}>Pulso</h2>
              <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap', marginBottom: space.lg }}>
                <KpiCard
                  label={pulso.es_hoy ? 'Hoy' : `El día (${fmtFecha(pulso.fecha)})`}
                  valor={fmtAbrev(pulso.dia?.facturacion ?? 0)}
                  sub={`${fmtNum(pulso.dia?.comprobantes ?? 0)} comprobantes · margen ${pctTxt(pulso.dia?.margen_pct).replace('+', '')}`}
                />
                <KpiCard
                  label="Mes acumulado"
                  valor={fmtAbrev(pulso.mes?.facturacion ?? 0)}
                  sub={`margen ${pctTxt(pulso.mes?.margen_pct).replace('+', '')} · ${fmtNum(pulso.mes?.clientes ?? 0)} clientes`}
                />
                {pulso.mes_anterior && (
                  <KpiCard
                    label={`Mes anterior · ${pulso.mes_anterior.etiqueta}`}
                    valor={fmtAbrev(pulso.mes_anterior.facturacion ?? 0)}
                    sub={pulso.mes_anterior.var != null ? `${pctTxt(pulso.mes_anterior.var)} vs mes previo` : 'cerrado'}
                  />
                )}
                <KpiCard
                  label="Proyección de cierre"
                  valor={pulso.proyeccion_total != null ? fmtAbrev(pulso.proyeccion_total) : '—'}
                  sub="suma de las curvas de cada canal"
                />
              </div>
              <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap' }}>
                {(pulso.canales || []).map((c) => <CanalCard key={c.grupo} c={c} />)}
              </div>
              {(pulso.top_subrubros_dia || []).length > 0 && (
                <div style={{ marginTop: space.lg, fontSize: text.sec, color: colors.textSec }}>
                  <span style={{ fontWeight: 600, color: colors.text }}>Top del día: </span>
                  {pulso.top_subrubros_dia.map((s) => `${s.subrubro.toLowerCase()} ${fmtAbrev(s.facturacion)}`).join(' · ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
